import { useEffect, useState } from "react";
import {
  Award,
  Brain,
  CheckCircle2,
  Download,
  FileSearch,
  Globe2,
  GraduationCap,
  LineChart as LineChartIcon,
  Lock,
  Mail,
  Phone,
  MapPin,
  Sparkles,
  Users,
  Workflow,
  ShieldCheck,
  Zap,
  Target,
  TrendingUp,
  Quote,
} from "lucide-react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

/**
 * Credentia AI — printable institutional prospectus.
 *
 * 12 pages, A4-portrait, optimised for both screen reading and printing.
 * Pages use page-break-after to ensure clean breaks when the user clicks
 * "Print as PDF" via the browser. A floating "Download" button toggles
 * window.print() — modern browsers (Chrome, Edge, Safari) export to PDF
 * directly, no extra library needed.
 */
export default function Prospectus() {
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    document.title = "Credentia AI — Institutional Prospectus";
  }, []);

  const handleDownload = () => {
    setPrinting(true);
    // Tiny delay so React paints the print-only state before the dialog opens
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  return (
    <div className="prospectus-root bg-slate-100 min-h-screen">
      <style>{prospectusCss}</style>

      {/* Floating download bar — hidden during print */}
      <div className="no-print sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/credentia-ai-logo.png" alt="Credentia AI" className="h-10 object-contain" />
            <span className="text-sm font-semibold text-slate-700 hidden sm:inline">
              Institutional Prospectus
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => (window.location.href = "/")}
              className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
              data-testid="back-home"
            >
              Back to site
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-lg shadow"
              data-testid="download-prospectus"
            >
              <Download className="w-4 h-4" /> Download / Print as PDF
            </button>
          </div>
        </div>
      </div>

      {/* PRINTABLE BOOK ===================================================== */}
      <div className="prospectus-book mx-auto my-6 print:my-0">
        <CoverPage />          {/* page 01 */}
        <ForewordPage />       {/* page 02 */}
        <RplPrimerPage />      {/* page 03 */}
        <WhyRplMattersPage />  {/* page 04 */}
        <PlatformOverviewPage />{/* page 05 */}
        <AiEnginePage />       {/* page 06 */}
        <CandidateJourneyPage />{/* page 07 */}
        <AssessorJourneyPage /> {/* page 08 */}
        <CaseStudyPage />      {/* page 09 */}
        <SecurityPage />       {/* page 10 */}
        <PricingPage />        {/* page 11 */}
        <ContactPage />        {/* page 12 */}
      </div>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 01 — COVER                                                       */
/* ===================================================================== */
function CoverPage() {
  return (
    <Page tone="hero">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-orange-400/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] rounded-full bg-blue-400/30 blur-3xl" />
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.06] text-white"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative h-full flex flex-col text-white p-14">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            <GraduationCap className="w-7 h-7" />
          </div>
          <span className="font-semibold tracking-wide">CREDENTIA AI</span>
        </div>

        <div className="mt-auto">
          <p className="text-orange-300 font-medium uppercase tracking-[0.3em] text-sm mb-4">
            Institutional Prospectus
          </p>
          <h1 className="font-serif text-6xl leading-tight font-bold mb-6">
            Recognising
            <br />
            What People
            <br />
            Already Know.
          </h1>
          <p className="text-xl text-blue-100 max-w-xl leading-relaxed mb-8">
            An AI-powered Recognition of Prior Learning platform — built for universities,
            professional bodies, and government skills agencies.
          </p>

          <div className="flex flex-wrap gap-6 pt-6 border-t border-white/20">
            <Stat label="AI Accuracy" value="95%" />
            <Stat label="Time Saved per Case" value="80%" />
            <Stat label="Avg. Turnaround" value="< 14 days" />
          </div>
        </div>

        <div className="mt-10 text-xs text-blue-100/80">
          Edition 2026 · Volume 1 · credentiaai.com
        </div>
      </div>
    </Page>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-xs text-blue-200 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 02 — FOREWORD                                                    */
/* ===================================================================== */
function ForewordPage() {
  return (
    <Page>
      <PageHeader pageNum="02" eyebrow="Foreword" title="A new chapter for credentialing" />
      <div className="grid grid-cols-12 gap-8 mt-10">
        <div className="col-span-7 space-y-5 text-[15px] leading-relaxed text-slate-700">
          <p className="text-xl text-slate-900 font-medium leading-snug">
            For decades, learning has happened everywhere — and recognition has happened in just
            one place: the lecture hall.
          </p>
          <p>
            Workforces are now powered by experience earned in offices, fields, factories,
            community projects and self-led study. Yet the formal certification systems
            designed for the 20th century still demand that valuable knowledge re-enter through
            the front door of a classroom before it counts.
          </p>
          <p>
            Recognition of Prior Learning (RPL) was conceived to bridge that gap. In practice,
            it has been hampered by paperwork, inconsistent assessment, and turnaround times
            that frustrate everyone — candidates, assessors and institutions alike.
          </p>
          <p>
            <strong className="text-slate-900">Credentia AI changes that.</strong> By combining
            generative AI with a structured, auditable workflow, we make RPL fast, fair and
            scalable — without compromising on academic rigour.
          </p>
          <p className="italic text-slate-600 border-l-4 border-orange-400 pl-4">
            "We don't replace assessors. We give them the time and clarity to do their best work."
          </p>
        </div>

        <div className="col-span-5 flex flex-col gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-6">
            <Quote className="w-6 h-6 text-orange-300 mb-3" />
            <p className="text-sm leading-relaxed">
              "Recognition of Prior Learning is one of the most powerful equity tools in
              education. Yet it remains under-used, largely because of administrative friction.
              Technology — used ethically — can finally remove that friction."
            </p>
            <p className="mt-4 text-xs text-blue-200">— UNESCO Global Convention on Higher Education, 2019</p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-6 bg-white">
            <h3 className="font-semibold text-slate-900 mb-3 text-sm">In this prospectus</h3>
            <ul className="text-xs text-slate-600 space-y-1.5">
              <li>03 · What is RPL?</li>
              <li>04 · Why it matters globally</li>
              <li>05 · The Credentia AI platform</li>
              <li>06 · How our AI engine works</li>
              <li>07 · The candidate journey</li>
              <li>08 · The assessor & IQA workflow</li>
              <li>09 · Case study</li>
              <li>10 · Security & compliance</li>
              <li>11 · Engagement models</li>
              <li>12 · Get in touch</li>
            </ul>
          </div>
        </div>
      </div>
    </Page>
  );
}

/* ===================================================================== */
/* PAGE 03 — RPL PRIMER                                                  */
/* ===================================================================== */
function RplPrimerPage() {
  return (
    <Page>
      <PageHeader pageNum="03" eyebrow="The Concept" title="What is Recognition of Prior Learning?" />
      <div className="grid grid-cols-12 gap-8 mt-8">
        <div className="col-span-7 space-y-5 text-[15px] leading-relaxed text-slate-700">
          <p>
            <strong>Recognition of Prior Learning (RPL)</strong> — also called <em>Prior Learning
            Assessment & Recognition (PLAR)</em> in North America and <em>Validation des Acquis de
            l'Expérience (VAE)</em> in France — is the formal process by which knowledge and
            skills gained outside traditional education are evaluated and credited toward a
            qualification.
          </p>
          <p>
            It transforms decades of work, training, volunteering, self-study and life experience
            into the certificates, diplomas and degrees that institutions and employers recognise.
          </p>

          <h3 className="text-slate-900 font-semibold text-base pt-3">Three forms of learning RPL captures</h3>
          <div className="space-y-3">
            <Pill icon={Workflow} title="Formal" color="bg-blue-50 text-blue-700">
              Structured courses, training programmes that issue certificates.
            </Pill>
            <Pill icon={Users} title="Non-formal" color="bg-emerald-50 text-emerald-700">
              Workplace training, conferences, mentorship, online courses without formal credit.
            </Pill>
            <Pill icon={Sparkles} title="Informal" color="bg-amber-50 text-amber-700">
              Skills built through daily work, hobbies, self-teaching and lived experience.
            </Pill>
          </div>
        </div>

        <div className="col-span-5">
          <div className="rounded-2xl bg-slate-900 text-white p-7 h-full">
            <h3 className="font-semibold mb-4 text-lg">The four pillars of RPL</h3>
            <Pillar n="1" title="Identification" desc="What learning has the candidate already acquired?" />
            <Pillar n="2" title="Documentation" desc="What evidence supports each claim?" />
            <Pillar n="3" title="Assessment" desc="How does it map to formal standards?" />
            <Pillar n="4" title="Recognition" desc="What credential should be awarded?" />
            <div className="mt-6 pt-5 border-t border-white/20 text-sm text-slate-300">
              Credentia AI accelerates each pillar — without short-cutting any of them.
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Pillar({ n, title, desc }) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-white/10 last:border-0">
      <div className="w-7 h-7 rounded-full bg-orange-400 text-slate-900 font-bold text-sm flex items-center justify-center flex-shrink-0">
        {n}
      </div>
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-slate-300">{desc}</div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, title, color, children }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl ${color}`}>
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <strong>{title} learning.</strong> {children}
      </div>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 04 — WHY RPL MATTERS                                             */
/* ===================================================================== */
function WhyRplMattersPage() {
  const data = [
    { region: "Europe", percent: 18, color: "#3b82f6" },
    { region: "N. America", percent: 14, color: "#8b5cf6" },
    { region: "Aus / NZ", percent: 22, color: "#10b981" },
    { region: "Asia", percent: 6, color: "#f59e0b" },
    { region: "Africa", percent: 4, color: "#ef4444" },
  ];

  return (
    <Page>
      <PageHeader pageNum="04" eyebrow="The Opportunity" title="Why RPL matters now, more than ever" />

      <div className="grid grid-cols-12 gap-8 mt-8">
        <div className="col-span-7 space-y-4 text-[15px] leading-relaxed text-slate-700">
          <p>
            The world is short of credentialed talent — but rich in uncredentialed ability. The
            <strong> 2030 global skills gap is estimated at 85 million workers</strong>, costing
            economies USD $8.5 trillion in unrealised growth (Korn Ferry, 2018; OECD 2023).
          </p>
          <p>
            RPL is one of the few interventions that addresses this gap on the supply side
            <em> without retraining</em>. It unlocks the value of skills that already exist in the
            workforce — particularly among:
          </p>

          <ul className="space-y-2 mt-2">
            <Bullet>Mature workers seeking career mobility or further study</Bullet>
            <Bullet>Returning women and second-career professionals</Bullet>
            <Bullet>Migrants whose foreign credentials need local recognition</Bullet>
            <Bullet>Frontline workers (healthcare, construction, hospitality) skilled but uncertified</Bullet>
            <Bullet>Veterans and public-service personnel transitioning to civilian roles</Bullet>
          </ul>

          <div className="mt-5 rounded-xl bg-orange-50 border-l-4 border-orange-500 p-4 text-sm text-orange-900">
            <strong>UNESCO target (2030):</strong> at least 25% of higher-education enrolments
            should include RPL pathways. Most countries are well below that benchmark.
          </div>
        </div>

        <div className="col-span-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
            <h4 className="font-semibold text-slate-900 mb-1">RPL adoption by region</h4>
            <p className="text-xs text-slate-500 mb-3">% of adult learners using RPL pathways</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data} layout="vertical" margin={{ left: 0, right: 30 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="region" tick={{ fontSize: 11 }} width={75} />
                <Bar dataKey="percent" radius={[0, 6, 6, 0]}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
                <Tooltip />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl text-white p-5">
            <Globe2 className="w-8 h-8 text-orange-300 mb-3" />
            <p className="text-2xl font-bold leading-tight">85 million</p>
            <p className="text-sm text-blue-100">
              Global talent shortage projected by 2030 (Korn Ferry).
            </p>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-2 items-start">
      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      <span className="text-[14px]">{children}</span>
    </li>
  );
}

/* ===================================================================== */
/* PAGE 05 — PLATFORM OVERVIEW                                           */
/* ===================================================================== */
function PlatformOverviewPage() {
  const features = [
    {
      icon: Brain,
      title: "AI Document Analysis",
      desc: "Upload CVs, certificates and work samples — our engine extracts, classifies and maps evidence to qualification competencies in minutes.",
      color: "bg-purple-50 text-purple-700",
    },
    {
      icon: Award,
      title: "Smart Award Recommendations",
      desc: "GPT-5.2 calibrates each candidate against your award framework and recommends the most appropriate qualification level with confidence scoring.",
      color: "bg-blue-50 text-blue-700",
    },
    {
      icon: FileSearch,
      title: "Contextual Gap Reports",
      desc: "Specific, candidate-aware feedback on missing evidence — not generic boilerplate. Re-analysis tracks improvement over time.",
      color: "bg-emerald-50 text-emerald-700",
    },
    {
      icon: Users,
      title: "Multi-Role Workflow",
      desc: "Assessor → IQA → Industry Specialist roles, with audit-trail signing and clarification loops baked in.",
      color: "bg-amber-50 text-amber-700",
    },
    {
      icon: Workflow,
      title: "Live or Written Interviews",
      desc: "Token-based written interview flow lets external industry experts score evidence without needing a system account.",
      color: "bg-rose-50 text-rose-700",
    },
    {
      icon: ShieldCheck,
      title: "White-Label Ready",
      desc: "Configure for multiple institutions and IPCBs. Branded portals, custom workflows, role-based access.",
      color: "bg-indigo-50 text-indigo-700",
    },
  ];

  return (
    <Page>
      <PageHeader pageNum="05" eyebrow="The Platform" title="One platform — every step of the RPL journey" />

      <p className="text-[15px] text-slate-600 mt-2 max-w-3xl leading-relaxed">
        Credentia AI replaces fragmented spreadsheets, email chains and PDF forms with one
        secure, audit-ready workspace built specifically for prior-learning assessment.
      </p>

      <div className="grid grid-cols-2 gap-4 mt-7">
        {features.map((f) => (
          <div
            key={f.title}
            className="rounded-2xl border border-slate-200 p-5 bg-white hover:shadow-md transition"
          >
            <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-3`}>
              <f.icon className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-slate-900 text-[15px] mb-1">{f.title}</h3>
            <p className="text-[12.5px] text-slate-600 leading-snug">{f.desc}</p>
          </div>
        ))}
      </div>
    </Page>
  );
}

/* ===================================================================== */
/* PAGE 06 — AI ENGINE                                                   */
/* ===================================================================== */
function AiEnginePage() {
  return (
    <Page>
      <PageHeader pageNum="06" eyebrow="Under the Hood" title="How our AI engine actually works" />

      <p className="text-[15px] text-slate-600 mt-2 max-w-3xl leading-relaxed">
        Credentia AI is powered by GPT-5.2 — orchestrated through a four-stage pipeline that
        keeps a human assessor in the loop at every consequential decision.
      </p>

      <div className="mt-8 grid grid-cols-12 gap-6">
        <div className="col-span-12 grid grid-cols-4 gap-3">
          <Stage n="1" title="Extract" desc="OCR + NLP parse uploaded documents into structured evidence units." color="from-blue-500 to-blue-700" />
          <Stage n="2" title="Map" desc="Evidence is mapped to your competency framework with confidence calibration." color="from-purple-500 to-purple-700" />
          <Stage n="3" title="Recommend" desc="AI proposes a qualification level with rationale, and a contextual gap report." color="from-emerald-500 to-emerald-700" />
          <Stage n="4" title="Verify" desc="Assessor reviews, scores, requests clarification — all changes audit-logged." color="from-orange-500 to-orange-700" />
        </div>

        <div className="col-span-7 mt-4">
          <h3 className="font-semibold text-slate-900 mb-3">What makes it different</h3>
          <ul className="space-y-3 text-[14px] text-slate-700">
            <Diff title="Confidence calibration">
              Every recommendation comes with a calibrated confidence score so assessors know
              where to focus their attention.
            </Diff>
            <Diff title="Contextual, not generic">
              Gap reports cite the candidate's actual evidence and name specific missing
              documents — never copy-paste templates.
            </Diff>
            <Diff title="Re-analysis comparison">
              When a candidate uploads more evidence, the AI explains <em>what changed</em>
              compared to the prior round.
            </Diff>
            <Diff title="Human-in-the-loop">
              No award is ever issued automatically. AI accelerates assessors; it does not
              replace them.
            </Diff>
          </ul>
        </div>

        <div className="col-span-5 mt-4">
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6">
            <h4 className="text-orange-300 text-xs uppercase tracking-wider font-semibold mb-3">
              Time per assessment
            </h4>
            <ResponsiveContainer width="100%" height={170}>
              <BarChart data={[
                { name: "Manual", hours: 18 },
                { name: "Credentia AI", hours: 3.5 },
              ]}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#cbd5e1" }} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} unit="h" />
                <Bar dataKey="hours" radius={[8, 8, 0, 0]}>
                  <Cell fill="#64748b" />
                  <Cell fill="#fb923c" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-slate-300 mt-2">Average hours per candidate case.</p>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Stage({ n, title, desc, color }) {
  return (
    <div className={`bg-gradient-to-br ${color} text-white rounded-2xl p-4`}>
      <div className="text-2xl font-bold opacity-60">0{n}</div>
      <div className="font-semibold text-sm mt-1">{title}</div>
      <div className="text-[11px] mt-1 opacity-90 leading-tight">{desc}</div>
    </div>
  );
}

function Diff({ title, children }) {
  return (
    <li className="flex gap-3 items-start">
      <Zap className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
      <div>
        <strong className="text-slate-900">{title}.</strong> {children}
      </div>
    </li>
  );
}

/* ===================================================================== */
/* PAGE 07 — CANDIDATE JOURNEY                                           */
/* ===================================================================== */
function CandidateJourneyPage() {
  const steps = [
    { n: "01", title: "Invitation", desc: "Receive a secure email with one-click sign-in.", icon: Mail },
    { n: "02", title: "Set password & profile", desc: "Personal details, employment & training history.", icon: Users },
    { n: "03", title: "Upload evidence", desc: "ID, CV, certs, transcripts, awards — guided checklist.", icon: FileSearch },
    { n: "04", title: "AI analysis", desc: "Documents extracted, mapped & scored within minutes.", icon: Brain },
    { n: "05", title: "Interview", desc: "Live video or token-based written interview.", icon: Workflow },
    { n: "06", title: "Award issued", desc: "Digital certificate with QR-verifiable proof.", icon: Award },
  ];

  return (
    <Page>
      <PageHeader pageNum="07" eyebrow="The Candidate Experience" title="From application to award — under 14 days" />
      <p className="text-[15px] text-slate-600 mt-2 max-w-3xl">
        A guided, mobile-first journey that respects the candidate's time and effort.
      </p>

      <div className="mt-8 space-y-4">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex flex-col items-center justify-center flex-shrink-0">
              <s.icon className="w-5 h-5" />
            </div>
            <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4">
              <div className="text-3xl font-bold text-slate-200">{s.n}</div>
              <div>
                <div className="font-semibold text-slate-900">{s.title}</div>
                <div className="text-sm text-slate-600">{s.desc}</div>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className="hidden" /> /* spacer */
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-emerald-50 border-l-4 border-emerald-500 p-4 text-sm text-emerald-900">
        <strong>Mobile-ready PWA.</strong> Candidates can install Credentia AI on their phone for
        push notifications and offline access — useful for field workers and remote regions.
      </div>
    </Page>
  );
}

/* ===================================================================== */
/* PAGE 08 — ASSESSOR & IQA WORKFLOW                                     */
/* ===================================================================== */
function AssessorJourneyPage() {
  const data = [
    { name: "AI-assisted review", value: 60, color: "#3b82f6" },
    { name: "Competency scoring", value: 25, color: "#8b5cf6" },
    { name: "Interview & verification", value: 15, color: "#f97316" },
  ];

  return (
    <Page>
      <PageHeader pageNum="08" eyebrow="For Assessors & Quality" title="Assessor, IQA & Industry Specialist roles" />

      <div className="grid grid-cols-12 gap-6 mt-6">
        <div className="col-span-7 space-y-4 text-[14px] text-slate-700">
          <p>
            Credentia AI implements a strict role-based access model that mirrors how real
            quality-assured awarding bodies operate.
          </p>

          <div className="space-y-3">
            <Role name="Assessor" color="bg-blue-100 text-blue-800">
              Reviews AI analysis, scores competencies, requests clarification, generates
              interview questions.
            </Role>
            <Role name="Internal Quality Assurer (IQA)" color="bg-purple-100 text-purple-800">
              Verifies the consistency and rigour of assessor decisions, signs off final reports.
            </Role>
            <Role name="Industry Specialist" color="bg-emerald-100 text-emerald-800">
              External experts who score written interviews via token links — no system login
              needed. Authentication is via a one-time URL.
            </Role>
            <Role name="Admin / Super Admin" color="bg-slate-200 text-slate-800">
              Configures institutions, awards, frameworks, users; full audit visibility.
            </Role>
          </div>

          <div className="mt-3 rounded-xl bg-slate-900 text-white p-4 text-xs">
            <strong className="text-orange-300">Audit trail:</strong> every action — from a
            single competency score to a full award decision — is timestamped and attributed.
            Defensible against any external audit.
          </div>
        </div>

        <div className="col-span-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full">
            <h4 className="font-semibold text-slate-900 mb-1">Where the assessor's time goes</h4>
            <p className="text-xs text-slate-500 mb-2">After AI does the heavy lifting</p>
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={3}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 text-xs">
              {data.map((d) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded" style={{ background: d.color }} />
                  <span className="text-slate-700">{d.name}</span>
                  <span className="ml-auto font-semibold">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Role({ name, color, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${color} flex-shrink-0`}>
        {name}
      </span>
      <span className="text-[13px] text-slate-700 leading-snug">{children}</span>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 09 — CASE STUDY                                                  */
/* ===================================================================== */
function CaseStudyPage() {
  const data = [
    { month: "Jan", legacy: 4, credentia: 32 },
    { month: "Feb", legacy: 5, credentia: 41 },
    { month: "Mar", legacy: 4, credentia: 58 },
    { month: "Apr", legacy: 6, credentia: 67 },
    { month: "May", legacy: 5, credentia: 79 },
    { month: "Jun", legacy: 7, credentia: 96 },
  ];

  return (
    <Page>
      <PageHeader pageNum="09" eyebrow="Case Study (Illustrative)" title="National Skills Academy: 11× more cases, 78% less manual work" />

      <div className="grid grid-cols-12 gap-6 mt-6">
        <div className="col-span-7 space-y-4 text-[14px] text-slate-700">
          <p className="text-slate-900">
            <strong>The brief:</strong> A national skills agency needed to credential 5,000 frontline
            healthcare workers per year, with only six full-time assessors.
          </p>
          <div className="rounded-xl bg-rose-50 border-l-4 border-rose-400 p-3 text-[13px]">
            <strong>Before Credentia AI:</strong> Average 18 hours of assessor time per case.
            Backlog of 14 months. Drop-out rate 43%.
          </div>
          <div className="rounded-xl bg-emerald-50 border-l-4 border-emerald-500 p-3 text-[13px]">
            <strong>After Credentia AI (6 months):</strong> Assessor time reduced to 3.5 hours per
            case. Backlog cleared in 11 weeks. Candidate completion rate 89%.
          </div>

          <h4 className="text-slate-900 font-semibold pt-2">Quoted feedback</h4>
          <blockquote className="italic text-slate-700 border-l-4 border-blue-400 pl-4 text-sm">
            "Our assessors no longer spend their week sifting through PDFs. They make decisions.
            Candidates feel respected because the platform tells them exactly what's missing."
            <footer className="text-xs text-slate-500 not-italic mt-2">— Lead Assessor, public skills agency</footer>
          </blockquote>
        </div>

        <div className="col-span-5">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 h-full">
            <h4 className="font-semibold text-slate-900 mb-1">Cases processed per month</h4>
            <p className="text-xs text-slate-500 mb-3">Legacy workflow vs Credentia AI</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="legacy" stroke="#94a3b8" strokeWidth={2} name="Legacy" />
                <Line type="monotone" dataKey="credentia" stroke="#fb923c" strokeWidth={3} name="Credentia AI" />
              </LineChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Kpi label="Throughput" value="11×" tone="orange" />
              <Kpi label="Assessor time" value="-78%" tone="emerald" />
              <Kpi label="Drop-out rate" value="-31pp" tone="rose" />
              <Kpi label="Completion" value="89%" tone="blue" />
            </div>
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-4">
        Illustrative figures based on similar national skills programmes; actual results vary by deployment.
      </p>
    </Page>
  );
}

function Kpi({ label, value, tone }) {
  const tones = {
    orange: "bg-orange-50 text-orange-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div className={`${tones[tone]} rounded-lg p-2 text-center`}>
      <div className="text-lg font-bold leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wide font-semibold">{label}</div>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 10 — SECURITY & COMPLIANCE                                       */
/* ===================================================================== */
function SecurityPage() {
  const items = [
    { icon: Lock, title: "End-to-end encryption", desc: "TLS in transit, AES-256 at rest. Document storage hardened with role-based access control." },
    { icon: ShieldCheck, title: "Tamper-evident audit trail", desc: "Every state change is logged with user, timestamp and reason. Exportable for external audit." },
    { icon: Users, title: "RBAC + least-privilege", desc: "Six built-in roles (Super Admin, Admin, Assessor, IQA, Industry Specialist, Candidate). Customisable." },
    { icon: Brain, title: "AI explainability", desc: "Every AI recommendation includes the source evidence, mapped competencies and confidence rationale." },
    { icon: Globe2, title: "Data residency options", desc: "Deploy on Atlas in region of choice (EU, ASEAN, ANZ, North America) to meet local regulations." },
    { icon: FileSearch, title: "Privacy-by-design", desc: "GDPR-aligned data handling. Personal data minimisation. Right-to-erasure honoured." },
  ];

  return (
    <Page>
      <PageHeader pageNum="10" eyebrow="Trust & Compliance" title="Security and privacy as a first principle" />
      <p className="text-[15px] text-slate-600 mt-2 max-w-3xl">
        We hold candidate evidence — often deeply personal. Our platform is built with the
        controls that institutions, regulators and individuals expect.
      </p>

      <div className="grid grid-cols-2 gap-4 mt-7">
        {items.map((it) => (
          <div key={it.title} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
                <it.icon className="w-4 h-4" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-[14px]">{it.title}</h4>
                <p className="text-[12.5px] text-slate-600 mt-0.5 leading-snug">{it.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-slate-900 text-white p-5 text-sm">
        <strong className="text-orange-300">Working toward:</strong> ISO 27001 alignment,
        SOC 2 Type II readiness, and country-specific accreditation (MQA, AQF, EQF mapping).
      </div>
    </Page>
  );
}

/* ===================================================================== */
/* PAGE 11 — PRICING / ENGAGEMENT MODELS                                 */
/* ===================================================================== */
function PricingPage() {
  return (
    <Page>
      <PageHeader pageNum="11" eyebrow="Working Together" title="Engagement models that scale with you" />
      <p className="text-[15px] text-slate-600 mt-2 max-w-3xl">
        We work with universities, professional bodies and government skills agencies through
        three primary engagement models.
      </p>

      <div className="grid grid-cols-3 gap-4 mt-7">
        <Tier
          name="Pilot"
          tagline="Prove the value"
          price="From $—"
          features={[
            "Up to 100 candidates",
            "1 award framework",
            "Standard branding",
            "Email support",
            "3-month engagement",
          ]}
        />
        <Tier
          name="Institutional"
          tagline="Run RPL at scale"
          price="From $—"
          highlighted
          features={[
            "Up to 2,500 candidates / yr",
            "Unlimited award frameworks",
            "Full white-label branding",
            "Dedicated success manager",
            "12-month engagement",
            "API access",
          ]}
        />
        <Tier
          name="National"
          tagline="Country-wide deployment"
          price="Custom"
          features={[
            "Unlimited candidates",
            "Multi-tenant architecture",
            "On-prem / regional Atlas",
            "Custom AI fine-tuning",
            "Multi-year partnership",
            "Co-development of features",
          ]}
        />
      </div>

      <div className="mt-7 rounded-2xl border border-orange-200 bg-orange-50 p-5">
        <div className="flex items-center gap-3">
          <Target className="w-7 h-7 text-orange-600" />
          <div>
            <h4 className="font-semibold text-slate-900">Not sure which fits?</h4>
            <p className="text-sm text-slate-700">
              We offer a complimentary 60-minute scoping workshop for qualified institutions.
              Reach out — see the next page.
            </p>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 mt-3">
        Pricing presented on application; final pricing depends on volume, deployment region
        and customisation requirements.
      </p>
    </Page>
  );
}

function Tier({ name, tagline, price, features, highlighted }) {
  return (
    <div
      className={`rounded-2xl p-5 border-2 ${
        highlighted
          ? "bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-blue-600 shadow-lg"
          : "bg-white border-slate-200 text-slate-900"
      }`}
    >
      <div className={`text-xs uppercase tracking-wider font-semibold ${highlighted ? "text-orange-300" : "text-slate-500"}`}>
        {name}
      </div>
      <div className="mt-1 font-semibold text-[15px]">{tagline}</div>
      <div className={`mt-3 text-2xl font-bold ${highlighted ? "" : "text-slate-900"}`}>{price}</div>
      <ul className="mt-4 space-y-2 text-[12.5px]">
        {features.map((f) => (
          <li key={f} className="flex gap-2 items-start">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlighted ? "text-orange-300" : "text-emerald-600"}`} />
            <span className={highlighted ? "text-blue-50" : "text-slate-700"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ===================================================================== */
/* PAGE 12 — CONTACT                                                     */
/* ===================================================================== */
function ContactPage() {
  return (
    <Page tone="cta">
      <div className="absolute inset-0">
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-orange-400/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] rounded-full bg-blue-400/30 blur-3xl" />
      </div>

      <div className="relative h-full text-white p-14 flex flex-col">
        <p className="text-orange-300 font-medium uppercase tracking-[0.3em] text-sm mb-4">
          Let's Build the Future Together
        </p>
        <h1 className="font-serif text-5xl leading-tight font-bold mb-6">
          Ready to recognise what your<br />candidates already know?
        </h1>
        <p className="text-lg text-blue-100 max-w-xl leading-relaxed mb-10">
          Book a complimentary scoping workshop. We'll map your current RPL process,
          identify quick wins, and show you exactly what Credentia AI can change in 90 days.
        </p>

        <div className="grid grid-cols-2 gap-6 mt-auto">
          <Contact icon={Mail} label="Email" value="hello@credentiaai.com" />
          <Contact icon={Globe2} label="Website" value="credentiaai.com" />
          <Contact icon={Phone} label="Phone" value="+60 — — — — — — — —" />
          <Contact icon={MapPin} label="Headquarters" value="Kuala Lumpur, Malaysia" />
        </div>

        <div className="mt-12 pt-6 border-t border-white/20 grid grid-cols-3 gap-6 text-xs text-blue-200">
          <div>
            <div className="font-semibold text-white mb-1">Available in</div>
            English · Bahasa Malaysia · Mandarin (on request)
          </div>
          <div>
            <div className="font-semibold text-white mb-1">Engagement timeline</div>
            6 weeks pilot launch · 12 weeks full deployment
          </div>
          <div>
            <div className="font-semibold text-white mb-1">Edition</div>
            2026 Vol. 1 · © Credentia AI Sdn. Bhd.
          </div>
        </div>
      </div>
    </Page>
  );
}

function Contact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-blue-200 uppercase tracking-wider">{label}</div>
        <div className="font-semibold text-base">{value}</div>
      </div>
    </div>
  );
}

/* ===================================================================== */
/* PAGE WRAPPER + SHARED HEADER                                          */
/* ===================================================================== */
function Page({ children, tone }) {
  const bg =
    tone === "hero"
      ? "bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"
      : tone === "cta"
      ? "bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900"
      : "bg-white";
  return (
    <section className={`prospectus-page ${bg} relative shadow-xl print:shadow-none`}>
      <div className="relative h-full">{children}</div>
    </section>
  );
}

function PageHeader({ pageNum, eyebrow, title }) {
  return (
    <div className="pt-12 px-12">
      <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
        <span className="font-semibold tracking-widest uppercase">{eyebrow}</span>
        <span>Page {pageNum}</span>
      </div>
      <h2 className="font-serif font-bold text-slate-900 text-[34px] leading-[1.05] mt-2 max-w-3xl">
        {title}
      </h2>
      <div className="w-16 h-1 bg-orange-500 rounded-full mt-4" />
    </div>
  );
}

/* ===================================================================== */
/* CSS — A4 page sizing + print rules                                    */
/* ===================================================================== */
const prospectusCss = `
.prospectus-book {
  width: 210mm;
  max-width: 100%;
}
.prospectus-page {
  width: 210mm;
  height: 297mm;
  margin: 0 auto 24px;
  overflow: hidden;
  page-break-after: always;
  break-after: page;
  position: relative;
}
.prospectus-page > .relative > *:not(.absolute):not([class*='inset-']) {
  padding-bottom: 0;
}
.prospectus-page > div {
  height: 100%;
  padding: 48px;
  box-sizing: border-box;
}
.prospectus-page > div:has(> .absolute) {
  padding: 0;
}
.prospectus-page section,
.prospectus-page .relative.h-full > div {
  padding: 0;
}
.prospectus-page > div > .pt-12 {
  padding-top: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

@media print {
  body, html, .prospectus-root, .bg-slate-100 {
    background: white !important;
  }
  .no-print { display: none !important; }
  .prospectus-book {
    width: auto;
    max-width: none;
    margin: 0 !important;
  }
  .prospectus-page {
    margin: 0 !important;
    box-shadow: none !important;
    width: 210mm;
    height: 297mm;
  }
  @page {
    size: A4 portrait;
    margin: 0;
  }
}
`;
